/**
 * Create Campaign hook.
 *
 * Orchestrates form validation, data building, and API submission for
 * campaign creation. Exposes submission state and a `markTouched` helper
 * so the page/form can drive inline, touched-based validation display.
 */

import { useState, useCallback, useRef } from 'react';
import { CampaignType, OperationStatus } from 'src/@types/valueMatching/enums';
import { CampaignConfigurationData } from 'src/@types/valueMatching/campaignConfiguration';
import { CampaignASIN } from 'src/@types/valueMatching/common';
import { UseCampaignFormResult } from 'src/hooks/useCampaignForm/useCampaignForm';
import { createCampaign } from 'src/accessors/valueMatchingAccessor';
import { buildCampaignDataFromForm } from 'src/utils/valueMatching/form/formDataBuilder';
import {
    validateCampaignForm,
    FieldErrors,
    FieldKey,
} from 'src/utils/valueMatching/form/formValidationUtils';
import { logError } from 'src/utils/monitoring';
import { useTranslate } from 'src/hooks/useTranslate';
import { useCampaignFormValidation } from 'src/hooks/useCampaignFormValidation';
import { ERROR_KEYS } from 'src/constants';

const DEFAULT_PROPAGATION_DELAY_MINUTES = 0;
const CREATE_CAMPAIGN_BUNDLE = 'pages.createCampaign';

export interface UseCreateCampaignResult {
    /** Triggers form validation → data build → API submission */
    submitCampaign: () => Promise<void>;
    /** Marks a field as touched so its error is shown from now on. */
    markTouched: (field: FieldKey) => void;
    /** True while the createCampaign API call is in-flight. Used to show a loading spinner and disable the submit button. Always `false` initially and after the call completes. */
    submitting: boolean;
    /** Non-field (submission/API) error message; `null` when there is no error (never `undefined`). */
    error: string | null;
    /**
     * Per-field error map for inline `errorText`. Visible only for touched
     * fields, unless the user has submitted at least once.
     * Always an object (never `null` or `undefined`); empty `{}` means no visible errors.
     */
    fieldErrors: FieldErrors;
    /** `true` after a successful campaign creation; `false` initially and on failure. */
    success: boolean;
    /** The server-assigned campaign ID after a successful creation; `null` initially and on failure (never `undefined`). */
    campaignId: string | null;
    /** ASINs that conflict with existing campaigns. Empty array `[]` when there are no conflicts (never `null` or `undefined`). */
    conflictingCampaignASINs: CampaignASIN[];
}

/**
 * Hook that manages the create-campaign submission lifecycle.
 *
 * Validation visibility rules:
 * - On load: no errors shown.
 * - Once a field is touched: its error becomes visible and updates live.
 * - On submit: all errors become visible regardless of touched state.
 *
 * @param form - The current form state from `useCampaignForm`. Always defined — the
 *   form hook initialises all fields to safe defaults (empty strings, `undefined` numbers, etc.).
 *
 * @param config - Server-provided campaign configuration fetched via `useCampaignConfiguration`.
 *   **Can be `null`** when the config API call is still in-flight or has failed. When `null`:
 *     - `propagationDelayInMinutes` defaults to 0 (no start-time guard).
 *     - `buildCampaignDataFromForm` skips brand-OFA mapping (no brand attributes).
 *   The hook is designed to be called unconditionally on every render (React rules of hooks),
 *   so it must tolerate `null` until the config loads.
 *
 * @param campaignType - The campaign type parsed from the URL route param (e.g., `NO_COST_EMI`).
 *   **Can be `undefined`** when:
 *     - The URL param is missing or contains an unrecognised value.
 *     - The page is still resolving the route.
 *   When `undefined`:
 *     - `allErrors` returns `{}` (no validation runs).
 *     - `submitCampaign` short-circuits with a user-facing error ("Campaign type is not available.").
 *     - No API call is made.
 */
export function useCreateCampaign(
    form: UseCampaignFormResult,
    config: CampaignConfigurationData | null,
    campaignType: CampaignType | undefined,
): UseCreateCampaignResult {
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const [campaignId, setCampaignId] = useState<string | null>(null);
    const [conflictingCampaignASINs, setConflictingCampaignASINs] = useState<CampaignASIN[]>([]);
    const { translate } = useTranslate(CREATE_CAMPAIGN_BUNDLE);

    const { fieldErrors, markTouched, markSubmitted } = useCampaignFormValidation({
        form,
        config,
        campaignType,
        formType: 'create',
    });

    // ── Concurrency guard ───────────────────────────────────────────────
    // `inFlightRef` prevents duplicate submissions when the user double-clicks
    // the submit button. A ref (not state) is used because we don't want a
    // re-render when flipping the flag — we just need a mutable boolean that
    // persists across renders.
    const inFlightRef = useRef(false);

    /**
     * Submit flow — called when the user clicks "Create Campaign".
     *
     * High-level flow:
     *   1. Guard against concurrent submissions (inFlightRef).
     *   2. Reset all state from any previous submission attempt.
     *   3. Bail out early if campaignType is not yet available.
     *   4. Run client-side validation; bail out if invalid (fieldErrors
     *      become visible via the `submitted` flag).
     *   5. Build the API payload from form state.
     *   6. POST to the BFF create-campaign endpoint.
     *   7. On SUCCESS → store campaignId, set success=true.
     *      On FAILURE → show generic error message.
     *   8. Surface any conflicting ASINs returned by the API.
     *   9. On network/unexpected error → log + show generic error.
     *  10. Always clear submitting spinner and release concurrency guard.
     */
    const submitCampaign = useCallback(async () => {
        // ── Step 1: Concurrency guard ───────────────────────────────────
        // If a submission is already in-flight, silently ignore the call.
        // This prevents duplicate API calls from rapid double-clicks.
        if (inFlightRef.current) return;
        inFlightRef.current = true;

        try {
        // ── Step 2: Reset all UI state ──────────────────────────────────
        // Clear stale results from any prior submission (success, error,
        // campaignId, conflicts) so the user sees a clean slate. We do
        // this at the very top — before any early returns — to guarantee
        // old data never lingers in the UI.
        markSubmitted();   // Flip the "has-submitted" flag so ALL field errors become visible.
        setSuccess(false);
        setCampaignId(null);
        setConflictingCampaignASINs([]);
        setError(null);

        // ── Step 3: Guard — campaign type must be defined ───────────────
        // campaignType is undefined when the URL param is invalid or the
        // config hasn't loaded yet. Show a user-facing error and bail.
        if (!campaignType) {
            setError(translate(ERROR_KEYS.CAMPAIGN_TYPE_UNAVAILABLE));
            return;
        }

        // ── Step 4: Client-side validation ──────────────────────────────
        // Run all field validators (title, ASINs, dates, type-specific
        // rules). If any fail, `validation.isValid` is false. The
        // `markSubmitted()` call above causes the shared validation hook to
        // surface ALL field errors regardless of touched state. We do NOT
        // call the API.
        const propagationDelay = config?.propagationDelayInMinutes ?? DEFAULT_PROPAGATION_DELAY_MINUTES;
        const validation = validateCampaignForm(form, campaignType, 'create', propagationDelay);

        if (!validation.isValid) return;

        // ── Step 5–8: API call ──────────────────────────────────────────
        // Validation passed → show the loading spinner and make the call.
        setSubmitting(true);

        try {
            // Step 5: Build the API request payload by transforming
            //   the form state (strings, arrays, booleans) into the
            //   CampaignData shape the BFF expects. This includes
            //   date→epoch conversion, ASIN parsing, and type-specific
            //   guardrail/attribute assembly.
            const campaignData = buildCampaignDataFromForm(form, campaignType, config);

            // Step 6: POST to the BFF create-campaign endpoint.
            //   The accessor handles CSRF tokens, fetch, and JSON parsing.
            const response = await createCampaign({ campaignData });

            // Step 7: Process the API response.
            //   The BFF returns an `operationStatus` enum:
            //   - SUCCESS → campaign was created. Capture the new
            //     campaignId so the page can show a success banner or
            //     redirect to the campaign detail view.
            //   - FAILURE (or any non-SUCCESS) → show a generic error
            //     message. We don't expose raw backend errors to the user.
            if (response.operationStatus === OperationStatus.SUCCESS) {
                setSuccess(true);
                setCampaignId(response.campaignId ?? null);
            } else {
                setError(translate(ERROR_KEYS.GENERIC_SUBMISSION));
            }

            // Step 8: Surface conflicting ASINs (if any).
            //   Regardless of success or failure, the BFF may return a
            //   list of ASINs that already belong to other active
            //   campaigns. We store them so the UI can render a conflict
            //   table below the form, letting the user decide what to do.
            if (response.conflictingCampaignASINs?.length) {
                setConflictingCampaignASINs(response.conflictingCampaignASINs);
            }
        } catch (err) {
            // ── Step 9: Handle network / unexpected errors ──────────────
            // Any exception from fetch (network timeout, DNS failure,
            // CSRF missing, JSON parse error, etc.) lands here.
            // We log the raw message for internal observability but
            // show a generic user-facing message to avoid leaking
            // internal details (URLs, stack traces, etc.).
            const message = err instanceof Error ? err.message : String(err);
            logError(message, { methodName: 'useCreateCampaign' });
            setError(translate(ERROR_KEYS.GENERIC_SUBMISSION));
        } finally {
            // ── Step 10: Always hide the spinner ────────────────────────
            // Whether the call succeeded, failed, or threw — stop
            // showing the loading indicator and re-enable the button.
            setSubmitting(false);
        }
        } finally {
            // Release the concurrency guard so subsequent clicks are
            // allowed (e.g., user fixes errors and resubmits).
            inFlightRef.current = false;
        }
    }, [form, campaignType, config, translate, markSubmitted]);

    return {
        submitCampaign,
        markTouched,
        submitting,
        error,
        fieldErrors,
        success,
        campaignId,
        conflictingCampaignASINs,
    };
}
